'use client';

import type { ICertification, ISimulation } from '@/types';
import { ROUTES } from '@/constants';
import { useGlobalStore } from '@/store/global-store';
import { useRouter } from 'next/navigation';
import {
  buildCertificationUrl,
  buildRoleplayUrl,
  buildSimulationCertUrl,
  buildSimulationPrepUrl,
  buildSimulationScoreUrl,
} from '@/domains/routing';

/**
 * Feature flag to enable the navigation retry logic for simulation-related routes.
 *
 * This is a temporary safeguard to mitigate observed navigation issues in certain
 * mobile webviews (notably iPad and browser automation in Sauce Labs) where
 * `router.push()` can fail silently. Enabling this flag activates a multi-attempt
 * retry mechanism that escalates from `router.push` to `router.replace` and finally
 * to `window.location.href` if necessary.
 */
const ENABLE_SIMULATION_NAV_RETRY = false;

/**
 * Note on navigation reliability:
 * In testing, we've observed that on certain mobile webviews (notably iPad and
 * browser automation in Sauce Labs), a `router.push()` call can fail silently,
 * resulting in the URL and page not updating while no error is thrown. This seems
 * to be a quirk of the Next.js client-side navigation system in these environments.
 */
const RETRY_TIMER_MS = 2000;

/**
 * Enabling this flag allows the fallback logic to eventually escalate to a hard
 * navigation via `window.location.href`, which is more likely to succeed but has
 * trade-offs (full page reload, loss of React state, etc.). This should be used
 * as a last resort when other navigation methods are unreliable in critical flows.
 */
const ENABLE_WINDOW_LOCATION_HREF = false;

/**
 * This hook centralizes all simulation-related navigation logic, providing a single
 * source of truth for how prep/roleplay/scorecard pages are linked together and how
 * global state is synced during navigation.
 */
const ENABLE_SIMULATION_NAV_LOCK = false;
const SIMULATION_NAV_LOCK_KEY = 'simulation-nav-lock-until'; // key for sessionStorage use
const SIMULATION_NAV_LOCK_MS = RETRY_TIMER_MS * 3 + 1000; // prevent back nav during retries

/**
 * Sets a short-lived lock in sessionStorage to prevent certain navigations
 * (e.g. back button) from triggering immediately after a simulation navigation.
 */
const setSimulationNavigationLock = () => {
  if (!ENABLE_SIMULATION_NAV_LOCK) return;
  if (typeof window === 'undefined') return;
  const lockUntil = Date.now() + SIMULATION_NAV_LOCK_MS;
  window.sessionStorage.setItem(SIMULATION_NAV_LOCK_KEY, lockUntil.toString());
};

/**
 * Checks if the simulation navigation lock is currently active by comparing the
 * current time to the stored lock expiration timestamp in sessionStorage.
 */
const isSimulationNavigationLockActive = () => {
  if (!ENABLE_SIMULATION_NAV_LOCK) return false;
  if (typeof window === 'undefined') return false;
  const rawValue = window.sessionStorage.getItem(SIMULATION_NAV_LOCK_KEY);
  if (!rawValue) return false;

  const lockUntil = Number(rawValue);
  if (Number.isNaN(lockUntil)) {
    window.sessionStorage.removeItem(SIMULATION_NAV_LOCK_KEY);
    return false;
  }

  const isActive = Date.now() < lockUntil;
  if (!isActive) {
    window.sessionStorage.removeItem(SIMULATION_NAV_LOCK_KEY);
  }

  return isActive;
};

// ================================
// useSimulationRouting
// ================================

/**
 * Custom hook that centralizes all simulation-related navigation logic.
 *
 * By consolidating routing in one place, components avoid duplicating URL
 * construction and global-state updates. Every navigation function:
 *   1. Optionally syncs the relevant slice(s) of global state.
 *   2. Delegates URL construction to the typed helpers in `@/domains/routing`.
 *   3. Issues a `router.push` (or `router.back`) via Next.js's `useRouter`.
 *
 * Uses the RESTful nested-route structure:
 *   /sales-rep/certifications/{certId}/simulations/{simId}/...
 *
 * @returns An object containing navigation handler functions.
 */
export function useSimulationRouting() {
  const router = useRouter();

  // On some mobile webviews (notably iPad/browser automation in Sauce Labs),
  // a button click can result in a stale/no-op client transition. We implement
  // multi-attempt retry logic:
  // 1. Try router.push() immediately (or router.replace() for simulation nav)
  // 2. Check at 2000ms, 4000ms, 8000ms intervals
  // 3. If route has not changed, escalate:
  //    - Attempt 2: try router.replace()
  //    - Attempt 3: try window.location.href (hard navigation)
  // 4. Log failures for debugging
  const safePushWithFallback = (url: string, isSimulationNav = false) => {
    if (isSimulationNav) {
      setSimulationNavigationLock();
    }

    // For simulation navigation specifically, prefer replace() since we don't
    // need to preserve back-stack in prep/roleplay flows.
    const expectedPath = url.split('?')[0];
    const startTime = Date.now();
    let attempt = 0;
    let retryTimer: number | null = null;

    const navigateAndVerify = () => {
      attempt++;

      // First attempt uses the specified method, subsequent attempts escalate
      if (attempt === 1) {
        if (isSimulationNav) {
          router.replace(url);
        } else {
          router.push(url);
        }
      } else if (attempt === 2) {
        // Second attempt: force replace
        router.replace(url);
      } else if (attempt === 3 && ENABLE_WINDOW_LOCATION_HREF) {
        // Final attempt: hard window.location for maximum certainty
        window.location.href = url;
      }
    };

    const checkAndRetry = () => {
      if (typeof window === 'undefined') return;

      const currentPath = window.location.pathname;
      const landedOnTarget = currentPath === expectedPath;

      if (!landedOnTarget) {
        const elapsed = Date.now() - startTime;

        // Allow up to 3 retry attempts
        if (attempt < 3) {
          navigateAndVerify();
          // Continue checking
          retryTimer = window.setTimeout(checkAndRetry, RETRY_TIMER_MS);
        } else {
          // All retries exhausted, log for debugging
          if (typeof console !== 'undefined') {
            console.warn(
              `[Navigation Safeguard] Failed to navigate to ${url} after ${elapsed}ms and ${attempt} attempts. Current location: ${currentPath}`,
            );
          }
        }
      } else {
        // Successfully landed, stop checking
        if (retryTimer) clearTimeout(retryTimer);
      }
    };

    // Kick off the navigation and verification process
    if (ENABLE_SIMULATION_NAV_RETRY) {
      // Initial navigation
      navigateAndVerify();

      // Start verification loop
      retryTimer = window.setTimeout(checkAndRetry, RETRY_TIMER_MS);
    } else {
      router.push(url);
    }
  };

  // Destructure only the store values this hook depends on, keeping the
  // dependency surface explicit and easy to trace in tests.
  const {
    // Only `personas` is needed to resolve the linked persona for a simulation;
    personas,
    // Only the setters are needed since this hook is responsible for
    // syncing global state before navigation; the actual values are not needed.
    setActiveCertification,
    setActivePersona,
    setActiveSimulation,
  } = useGlobalStore();

  // ================================
  // Navigation Handlers
  // ================================

  /**
   * Navigate to the certification overview page.
   *
   * Sets the active certification in global state before navigating so that
   * any downstream page can read the cert without an additional fetch.
   *
   * @param certification - The certification to make active and navigate to.
   * @returns The result of `router.push` (a Promise<void> in Next.js App Router).
   */
  const navigateToCertification = (certification: ICertification) => {
    setActiveCertification(certification);

    const url = buildCertificationUrl(certification.uuid);
    return safePushWithFallback(url);
  };

  /**
   * Navigate to the simulation prep page for a given certification + simulation pair.
   *
   * Before navigating, this function:
   *   - Looks up the persona associated with the simulation (falls back to `null`
   *     when no match exists in the store, e.g. stale/missing data).
   *   - Syncs `activeSimulation`, `activePersona`, and `activeCertification` in
   *     global state so prep-page components can render immediately without
   *     re-fetching.
   *
   * @param certification - The parent certification that owns this simulation.
   * @param simulation    - The simulation the user wants to prepare for.
   * @returns The result of `router.push`.
   */
  const navigateToSimulation = (certification: ICertification, simulation: ISimulation) => {
    // Resolve the persona linked to this simulation. If no match is found
    // (e.g. the persona was deleted), default to null so the prep page can
    // render a graceful fallback.
    const persona = personas.find((p) => p.id === simulation.personaId) ?? null;

    // Sync all three related slices of global state before navigating.
    setActiveSimulation(simulation);
    setActivePersona(persona);
    setActiveCertification(certification);

    // IDs must be strings for the URL builder; certification uses numeric `id`
    // (not `uuid`) to match the route parameter contract.
    const certId = certification.id.toString();
    const simId = simulation.id.toString();

    // Delegate URL construction to the typed helper that enforces the required route
    const url = buildSimulationPrepUrl(certId, simId);
    return safePushWithFallback(url, true); // isSimulationNav = true for aggressive retry
  };

  /**
   * Navigate to the live roleplay page for a simulation.
   *
   * Global state is assumed to already be populated (e.g. set during the
   * preceding `navigateToSimulation` call), so no state updates are made here.
   *
   * @param certificationId - The string ID of the parent certification.
   * @param simulationId    - The string ID of the simulation.
   * @returns The result of `router.push`.
   */
  const navigateToRoleplay = (certificationId: string, simulationId: string) => {
    const url = buildRoleplayUrl(certificationId, simulationId);
    return safePushWithFallback(url, true); // isSimulationNav = true for aggressive retry
  };

  /**
   * Navigate to the simulation score / results page.
   *
   * Optional query parameters are forwarded to the URL builder, which
   * serialises them as a standard query string (e.g. `?sessionId=42`).
   * When no params are provided the URL is returned without a `?`.
   *
   * @param certificationId - The string ID of the parent certification.
   * @param simulationId    - The string ID of the simulation.
   * @param queryParams     - Optional key-value pairs to append as query params.
   * @returns The result of `router.push`.
   */
  const navigateToSimulationResults = (
    certificationId: string,
    simulationId: string,
    queryParams?: Record<string, string>,
  ) => {
    const url = buildSimulationScoreUrl(certificationId, simulationId, queryParams);
    return safePushWithFallback(url, true); // isSimulationNav = true for aggressive retry
  };

  /**
   * Navigate to the simulation completion certificate page.
   *
   * Shown after a user successfully passes a certification's simulations.
   *
   * @param certificationId - The string ID of the certification whose certificate to display.
   * @returns The result of `router.push`.
   */
  const navigateToSimulationCertificate = (certificationId: string) => {
    const url = buildSimulationCertUrl(certificationId);
    return safePushWithFallback(url);
  };

  /**
   * Navigate to the sales-rep dashboard (`/sales-rep`).
   *
   * Used as the primary "home" destination for sales-rep role users.
   *
   * @returns The result of `router.push`.
   */
  const navigateToDashboard = () => {
    const isWithinCertificationFlow =
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/sales-rep/certifications/');

    if (isWithinCertificationFlow && isSimulationNavigationLockActive()) {
      return;
    }

    const url = ROUTES.SALES_REP_DASHBOARD;
    return safePushWithFallback(url);
  };

  /**
   * Navigate to the manager dashboard (`/manager`).
   *
   * Used as the primary "home" destination for manager role users.
   *
   * @returns The result of `router.push`.
   */
  const navigateToManagerDashboard = () => {
    const url = ROUTES.MANAGER_DASHBOARD;
    return safePushWithFallback(url);
  };

  /**
   * Navigate back to the previous page in the browser history stack.
   *
   * Wraps `router.back()` so consumers don't need to import `useRouter`
   * directly; keeps all navigation concerns in one hook.
   *
   * @returns The result of `router.back`.
   */
  const navigateBack = () => {
    return router.back();
  };

  /**
   * Navigate to the login page (`/auth/login`).
   *
   * Typically called after an auth error or when the session expires.
   *
   * @returns The result of `router.push`.
   */
  const navigateToLogin = () => {
    const url = ROUTES.LOGIN;
    return safePushWithFallback(url);
  };

  // ================================
  // Public API
  // ================================

  // All handlers are returned alphabetically to keep the public surface
  // consistent and easy to scan.
  return {
    navigateBack,
    navigateToCertification,
    navigateToDashboard,
    navigateToLogin,
    navigateToManagerDashboard,
    navigateToRoleplay,
    navigateToSimulation,
    navigateToSimulationCertificate,
    navigateToSimulationResults,
  };
}
